import { observer } from "mobx-react-lite"
import * as yup from "yup"
import styles from "../sharedStyles.module.scss"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import PersonFill from "~icons/eva/person-fill"
import { useRef, useMemo } from "react"
import { getAvatarDownloadUrl } from "../../../../../services/firestore/userAvatar"
import { makeAutoObservable } from "mobx"
import { uploadAvatar } from "../../../../../services/firestore/userAvatar"
import { getAuth } from "firebase/auth"
import FormTextArea from "../../../../atoms/FormTextArea"
import EditFilledIcon from "../../../../assets/icons/EditFilledIcon"
import ArrowBackOutline from "~icons/eva/arrow-back-outline"
import { colors } from "@theme/colors"
import FormTextInput from "../../../../atoms/FormTextInput"

type ProfileInformationForm = {
  shortBio: string
  favoriteTravelDestination: string
  bestTravelAdvice: string
}

class ProfileInformationStore {
  avatarImageUrl: string | null

  constructor(avatar?: string) {
    this.avatarImageUrl = avatar || null

    makeAutoObservable(this)
  }

  setAvatarImageUrl(url: string) {
    this.avatarImageUrl = url
  }
}

const profileInformationSchema = yup.object().shape({
  shortBio: yup.string().required("-"),
  favoriteTravelDestination: yup.string().required("-"),
  bestTravelAdvice: yup.string().required("-"),
})

export const ProfileInformation = observer(
  ({
    onSubmit,
    goBack,
    formValues,
  }: {
    onSubmit: (data: ProfileInformationForm) => void
    goBack: (data: ProfileInformationForm & { avatar: string }) => void
    formValues: ProfileInformationForm & { avatar: string }
  }) => {
    const avatarInputRef = useRef<HTMLInputElement | null>(null)
    const store = useMemo(() => new ProfileInformationStore(formValues.avatar), [])
    const {
      control,
      formState: { isValid },
      handleSubmit,
      getValues,
    } = useForm({
      defaultValues: {
        favoriteTravelDestination: formValues.favoriteTravelDestination,
        bestTravelAdvice: formValues.bestTravelAdvice,
        shortBio: formValues.shortBio,
      },
      resolver: yupResolver(profileInformationSchema),
    })

    const handleSubmitProfileInformation = (data: ProfileInformationForm) => {
      const newUserPayload = {
        shortBio: data.shortBio,
        favoriteTravelDestination: data.favoriteTravelDestination,
        bestTravelAdvice: data.bestTravelAdvice,
        avatar: store.avatarImageUrl || "",
      }

      onSubmit(newUserPayload)
    }

    const currentUser = getAuth().currentUser

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const selectedFile = event.target.files[0]
        await uploadAvatar(currentUser?.uid, selectedFile)
        const url = await getAvatarDownloadUrl(currentUser?.uid)
        store.setAvatarImageUrl(url)
      }
    }

    return (
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit(handleSubmitProfileInformation)()
        }}
      >
        <div className={styles.inputsContainer}>
          <input
            type="file"
            ref={avatarInputRef}
            style={{ display: "none" }}
            onChange={(e) => handleFileChange(e)}
            accept=".jpg,.jpeg,.png"
          />
          <p className={styles.uploadPhotoLabel}>Photo</p>
          <button
            className={styles.uploadPhotoContainer}
            type="button"
            onClick={() => {
              if (avatarInputRef.current) {
                avatarInputRef.current.click()
              }
            }}
          >
            {store.avatarImageUrl ? (
              <img src={store.avatarImageUrl} alt="User avatar" className={styles.avatarImage} />
            ) : (
              <div className={styles.personIconContainer}>
                <PersonFill className={styles.personIcon} />
              </div>
            )}
            <div className={styles.editIconFullContainer}>
              <div className={styles.editIconContainer}>
                <EditFilledIcon height={8} width={8} color={colors.iconPrimary} />
              </div>
            </div>
          </button>
          <FormTextInput
            autoFocus
            control={control}
            testID="favorite-travel-destination-input"
            name="favoriteTravelDestination"
            label="Favorite travel destination*"
            parentBackgroundColor={colors.backgroundSecondary}
          />
          <FormTextInput
            control={control}
            testID="best-travel-advice-input"
            name="bestTravelAdvice"
            label="Best travel advice*"
            parentBackgroundColor={colors.backgroundSecondary}
          />
          <FormTextArea
            control={control}
            testID="short-bio-input"
            name="shortBio"
            label="Short bio*"
            rows={4}
            resize={false}
            parentBackgroundColor={colors.backgroundSecondary}
            className={styles.shortBioInput}
          />
        </div>
        <button
          type="submit"
          className={styles.button}
          disabled={!isValid}
          data-testid="go-to-agency-info-button"
        >
          Next
        </button>
        <button
          type="button"
          className={styles.previousButton}
          data-testid="go-to-personal-details-button"
          onClick={() => {
            const values = getValues()
            goBack({
              shortBio: values?.shortBio || "",
              favoriteTravelDestination: values?.favoriteTravelDestination || "",
              bestTravelAdvice: values?.bestTravelAdvice || "",
              avatar: store.avatarImageUrl || "",
            })
          }}
        >
          <ArrowBackOutline className={styles.previousIcon} /> Previous page
        </button>
      </form>
    )
  },
)
